import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { Text, Button, ConfirmModal } from '../../components';
import BulkMessageEditor from './BulkMessageEditor';
import MessagePreview from './MessagePreview';
import {
  sendErrorMessages,
  commonStrings,
  sendAllMessageStrings,
  routes,
  DATE_FORMAT,
} from '../../constants';
import {
  getDraftMessage,
  getReservation,
  saveDraftMessage,
  sendMessageForAll,
  sendTestMessageForAll,
  updateDraftMessage,
  updateReservation,
  getDeliveryCount,
  getTestDeliveryCount,
} from '../../api/messages';
import { getAutoReplyMessageAssets } from '../../api';
import useUploadFile from '../../hooks/useUploadFile';
import { useOrganizationToast } from '../../hooks/useOrganizationToast';
import { useLineDispatchRecovery } from '../../hooks/useLineDispatchRecovery';
import { useMountedRef } from '../../hooks/useMountedRef';
import { useOrgPath } from '../../hooks/useOrgPath';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useAuth } from '../../contexts/AuthContext';
import {
  useOrganizationBusy,
  useOrganizationId,
  useUnsavedInputGuard,
} from '../../components/OrganizationScope/context';
import { buildSendOperationOwnerKey } from '../../utils/organizationStorage';
import { getHttpStatus } from '../../utils';
import { isOrganizationFeatureEnabled } from '../../utils/organizationFeature';

import {
  EContentSectionType,
  EOrganizationFeature,
  ESendRecipient,
  ESendTime,
  TMessageSection,
  TMessageSendOption,
} from '../../types';
import { queryKeys } from '../../constants/queryKeys';
import {
  areMessageAssetSectionsValid,
  createSection,
  hasAnyContent,
} from 'utils/messageSection';
import {
  convertDraftSectionsToMessageSections,
  formatSectionsForAPI,
  normalizeDraftSendOption,
} from './utils';
import { SaveDraftModal } from './Drafts/SaveDraftModal';
import { MessageAssetQueryError } from '../../components/MessageAssetQueryError';
import { pageCardClasses } from '../../components/AppLayout/styles';
import {
  claimSendOperationIdentity,
  isSendOperationPayloadMismatch,
  clearSendOperationIdentity,
  clearSendOperationIdentityByIntent,
  createLineDispatchIntentHash,
} from './sendOperationIdentity';

/**
 * タグ選択の順序・重複を吸収し、同じ対象集合が常に同じ送信内容になるようにする。
 * 送信identity(clientMessageId)の判定とAPIリクエストの両方でこの値を使う。
 */
function normalizeTagIds(tagIds: number[] | undefined): number[] | undefined {
  if (!tagIds) return tagIds;
  return [...new Set(tagIds)].sort((a, b) => a - b);
}

type PendingSendOperation = {
  clientMessageId: string;
  sourceKey: string;
  sourceRevision: number;
  intentHash: string;
  requestedIntentHash: string;
  sectionsFingerprint: string;
  optionsFingerprint: string;
  formattedSections: Awaited<ReturnType<typeof formatSectionsForAPI>>;
  currentDraftId?: string;
};

/**
 * 実際にAPIへ送るpayloadが前回の操作と同一かを判定する。
 * 同一なら同じ操作の再試行なので clientMessageId を維持し、
 * サーバ側の冪等判定に同じIDを渡して二重配信を防ぐ。
 */
function isSameSendPayload(
  previous: PendingSendOperation | null,
  next: {
    optionsFingerprint: string;
    formattedSections: PendingSendOperation['formattedSections'];
  },
): previous is PendingSendOperation {
  return (
    previous !== null &&
    previous.optionsFingerprint === next.optionsFingerprint &&
    JSON.stringify(previous.formattedSections) ===
      JSON.stringify(next.formattedSections)
  );
}

/**
 * 再描画で配列参照だけが変わっても同じ送信意図を再利用できるよう、入力内容を比較可能にする。
 * File は JSON 化されないため、送信内容を識別できるメタデータへ明示的に変換する。
 */
function createSectionsFingerprint(sections: TMessageSection[]): string {
  return JSON.stringify(sections, (_key, value) => {
    if (typeof File !== 'undefined' && value instanceof File) {
      return {
        name: value.name,
        size: value.size,
        type: value.type,
        lastModified: value.lastModified,
      };
    }
    return value;
  });
}

// 配信対象ごとの送信確認文言
/** 予約（未来日時指定）は送信ではなく保存なので、確認文言を送信用と分ける */
const SCHEDULE_TITLE_BY_RECIPIENT: Record<ESendRecipient, string> = {
  [ESendRecipient.ALL]: sendAllMessageStrings.schedule.titleAll,
  [ESendRecipient.FILTER]: sendAllMessageStrings.schedule.titleFiltered,
  [ESendRecipient.UNTAGGED]: sendAllMessageStrings.schedule.titleUntagged,
};

const CONFIRM_STRINGS_BY_RECIPIENT: Record<
  ESendRecipient,
  { title: string; message: string }
> = {
  [ESendRecipient.ALL]: {
    title: sendAllMessageStrings.titleSendCustomer,
    message: sendAllMessageStrings.contentSendCustomer,
  },
  [ESendRecipient.FILTER]: {
    title: sendAllMessageStrings.titleSendFiltered,
    message: sendAllMessageStrings.contentSendFiltered,
  },
  [ESendRecipient.UNTAGGED]: {
    title: sendAllMessageStrings.titleSendUntagged,
    message: sendAllMessageStrings.contentSendUntagged,
  },
};

function SendMessageForAll(): JSX.Element {
  const { user: authUser } = useAuth();
  // logoutでlocalStorageは消えないため、共有PCで前任者のsource keyを
  // 引き継がないよう保存済みidentityを利用者×組織単位に閉じる。
  const organizationId = useOrganizationId();
  // 組織切り替えを安全に止めるため、mutation以外の直接API操作をbusy登録する
  const { beginBusy, endBusy } = useOrganizationBusy();
  const sendOperationOwnerKey = authUser
    ? buildSendOperationOwnerKey(authUser.uid, organizationId)
    : '';
  const [sections, setSections] = useState<TMessageSection[]>(() => [
    createSection(EContentSectionType.TEXT),
  ]);
  const [canSaveDraft, setCanSaveDraft] = useState(false);
  // 送信・下書き保存していない本文がある間は組織切り替えの前に破棄確認を挟ませる
  useUnsavedInputGuard(() => canSaveDraft);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const draftId = searchParams.get('draftId');
  const reservationId = searchParams.get('reservationId');
  const [sendOption, setSendOption] = useState<TMessageSendOption>({
    recipient: ESendRecipient.ALL,
    sendTime: ESendTime.IMMEDIATE,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isTestSending, setIsTestSending] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState<string | undefined>(
    undefined,
  );
  // 配信予約の編集中はこのIDが入る。送信ではなく既存の予約を更新する
  const [editingReservationId, setEditingReservationId] = useState<
    string | undefined
  >(undefined);
  const [isLoadingReservation, setIsLoadingReservation] = useState(false);
  const [isUpdatingReservation, setIsUpdatingReservation] = useState(false);
  const [openReservationConfirm, setOpenReservationConfirm] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openTestConfirm, setOpenTestConfirm] = useState(false);
  // テスト配信確認モーダルに表示する人数。refetch の最新結果を固定し、
  // hook スナップショット（stale/null）ではなくこの値を表示に使う。
  const [confirmTestCount, setConfirmTestCount] = useState<number | null>(null);
  const [openSaveModal, setOpenSaveModal] = useState(false);
  const pendingBulkSendRef = useRef<PendingSendOperation | null>(null);
  const pendingTestSendRef = useRef<PendingSendOperation | null>(null);
  const { uploadToS3 } = useUploadFile();
  const { toastError, toastSuccess } = useOrganizationToast();
  const { recover: recoverLineDispatch, acknowledgeProducerResult } =
    useLineDispatchRecovery();
  // 戻る/進むで画面を離れた後に完了した操作は、新しい画面を動かさない（遷移・画面の状態）。結果のトーストは出す
  const isMountedRef = useMountedRef();

  // 配信予約を読み込んでいる間は、まだ予約編集モードに入らない
  const isEditingReservation = Boolean(editingReservationId);

  // URLに予約IDがある間は、編集モードに入る前でも一斉送信・テスト配信を止める。
  // 取得を待つ間に本文を打って送信すると、予約とは別の新規配信が飛んでしまう。
  // 初回描画（effect実行前）も塞ぐため、stateではなくURLから導出する。
  const isReservationLoadPending =
    Boolean(reservationId) && !isEditingReservation;

  // recipient=FILTER のときだけ対象タグで配信数を絞り込む。未選択なら配信数取得を止める。
  const filterTagIds =
    sendOption.recipient === ESendRecipient.FILTER
      ? (sendOption.tagIds ?? [])
      : undefined;

  const { data: currentUser } = useCurrentUser();
  const isRecipientUnavailable =
    sendOption.recipient !== ESendRecipient.ALL &&
    !isOrganizationFeatureEnabled(
      currentUser,
      EOrganizationFeature.TAG_MANAGEMENT,
    );

  // 未来日時の指定は「送信」ではなく予約の保存なので、確認文言も切り替える
  const isScheduledSend = sendOption.sendTime === ESendTime.SCHEDULED;

  const confirmStrings = useMemo(() => {
    if (!isScheduledSend) {
      return CONFIRM_STRINGS_BY_RECIPIENT[sendOption.recipient];
    }
    return {
      title: SCHEDULE_TITLE_BY_RECIPIENT[sendOption.recipient],
      message: sendAllMessageStrings.schedule.message.replace(
        '${datetime}',
        sendOption.scheduledDateTime
          ? moment(sendOption.scheduledDateTime).format(
              `${DATE_FORMAT} HH:mm`,
            )
          : '',
      ),
    };
  }, [
    isScheduledSend,
    sendOption.recipient,
    sendOption.scheduledDateTime,
  ]);

  const shouldFetchDeliveryCount =
    !isRecipientUnavailable &&
    (sendOption.recipient !== ESendRecipient.FILTER ||
      (filterTagIds?.length ?? 0) > 0);
  const {
    data: deliveryCountData,
    isSuccess: isDeliveryCountSuccess,
    isPending: isDeliveryCountPending,
    isError: isDeliveryCountQueryError,
    refetch: refetchDeliveryCount,
  } = useQuery({
    queryKey: [queryKeys.DELIVERY_COUNT, sendOption.recipient, filterTagIds],
    queryFn: () =>
      getDeliveryCount({
        recipient: sendOption.recipient,
        ...(filterTagIds && filterTagIds.length > 0
          ? { tagIds: filterTagIds }
          : {}),
      }),
    enabled: shouldFetchDeliveryCount,
  });

  const { data: testDeliveryCountData, refetch: refetchTestDeliveryCount } =
    useQuery({
      queryKey: [queryKeys.TEST_DELIVERY_COUNT],
      queryFn: () => getTestDeliveryCount(),
    });

  const {
    data: assets = [],
    isSuccess: assetsReady,
    isError: assetsLoadError,
    isFetching: isFetchingAssets,
    refetch: refetchAssets,
  } = useQuery({
    queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS],
    queryFn: () => getAutoReplyMessageAssets(),
  });

  const deliveryCount = useMemo(
    () => deliveryCountData?.count ?? null,
    [deliveryCountData],
  );
  const isDeliveryCountLoading =
    shouldFetchDeliveryCount && isDeliveryCountPending;
  const isDeliveryCountError =
    shouldFetchDeliveryCount && isDeliveryCountQueryError;
  const isDeliveryCountReady =
    shouldFetchDeliveryCount &&
    isDeliveryCountSuccess &&
    deliveryCount !== null &&
    deliveryCount > 0;

  const testDeliveryCount = useMemo(
    () => testDeliveryCountData?.count ?? null,
    [testDeliveryCountData],
  );

  const formatSections = useCallback(
    async (sections: TMessageSection[]) => {
      return formatSectionsForAPI(sections, {
        uploadToS3,
        onError: toastError,
      });
    },
    [uploadToS3, toastError],
  );

  const validateBulkMessageForm = useCallback(
    (sectionsArg: TMessageSection[]): boolean => {
      if (!hasAnyContent(sectionsArg)) {
        toastError(new Error(sendErrorMessages.noMediaInput));
        return false;
      }

      if (!areMessageAssetSectionsValid(sectionsArg, assets, assetsReady)) {
        toastError(new Error(sendErrorMessages.invalidAssetSelection));
        return false;
      }

      if (
        sendOption.recipient === ESendRecipient.FILTER &&
        (sendOption.tagIds?.length ?? 0) === 0
      ) {
        toastError(new Error(sendErrorMessages.recipientTagsRequired));
        return false;
      }

      if (sendOption.sendTime === ESendTime.SCHEDULED) {
        if (!sendOption.scheduledDateTime) {
          toastError(new Error(sendErrorMessages.scheduledDateTimeRequired));
          return false;
        }

        const scheduledMoment = moment(sendOption.scheduledDateTime);

        if (!scheduledMoment.isAfter(moment())) {
          toastError(new Error(sendErrorMessages.scheduledDateTimeInPast));
          return false;
        }
      }

      return true;
    },
    [assets, assetsReady, sendOption, toastError],
  );

  // 本配信だけに適用する。配信対象数の取得状態で下書き保存は妨げない。
  const validateDeliveryTarget = useCallback((): boolean => {
    if (isRecipientUnavailable) {
      toastError(new Error(sendErrorMessages.recipientUnavailable));
      return false;
    }
    if (!isDeliveryCountReady) {
      toastError(
        new Error(
          deliveryCount === 0
            ? sendAllMessageStrings.noDeliverableUsers
            : sendAllMessageStrings.deliveryCountLoadFailed,
        ),
      );
      return false;
    }
    return true;
  }, [deliveryCount, isDeliveryCountReady, isRecipientUnavailable, toastError]);

  const handleSendClick = useCallback(() => {
    if (!validateBulkMessageForm(sections)) return;
    if (!validateDeliveryTarget()) return;
    setOpenConfirm(true);
  }, [sections, validateBulkMessageForm, validateDeliveryTarget]);

  const handleSend = useCallback(async () => {
    beginBusy();
    try {
      setIsLoading(true);

      if (!validateBulkMessageForm(sections)) {
        return;
      }
      if (!validateDeliveryTarget()) {
        return;
      }

      const apiSendOption = {
        recipient: sendOption.recipient,
        // [A,B]と[B,A]は同じ対象集合。選択順の違いで別の送信IDが発行されると、
        // 応答消失後の再試行で同じ受信者へ重複配信されるため正規化する
        tagIds:
          sendOption.recipient === ESendRecipient.FILTER
            ? normalizeTagIds(sendOption.tagIds)
            : undefined,
        sendTime: sendOption.sendTime,
        scheduledDateTime:
          sendOption.scheduledDateTime == null
            ? undefined
            : typeof sendOption.scheduledDateTime === 'string'
              ? sendOption.scheduledDateTime
              : sendOption.scheduledDateTime.toISOString(),
      };
      const sectionsFingerprint = createSectionsFingerprint(sections);
      const optionsFingerprint = JSON.stringify(apiSendOption);
      let operation = pendingBulkSendRef.current;
      if (
        !operation ||
        operation.sectionsFingerprint !== sectionsFingerprint ||
        operation.optionsFingerprint !== optionsFingerprint
      ) {
        const formattedSections = await formatSections(sections);
        if (formattedSections.length === 0) {
          toastError(new Error(sendErrorMessages.noContent));
          return;
        }
        const hasSamePayload = isSameSendPayload(operation, {
          optionsFingerprint,
          formattedSections,
        });
        const samePayloadOperation = hasSamePayload ? operation : null;
        const operationDraftId =
          samePayloadOperation?.currentDraftId ??
          (operation ? undefined : currentDraftId);
        if (!samePayloadOperation && operation?.currentDraftId) {
          setCurrentDraftId(undefined);
        }
        const intentHash = await createLineDispatchIntentHash({
          sections: formattedSections,
          sendOption: apiSendOption,
        });
        const identity = await claimSendOperationIdentity({
          kind: 'bulk',
          intentHash,
          sourceRevision: 1,
          ownerKey: sendOperationOwnerKey,
          ...(operationDraftId ? { sourceKeyOverride: operationDraftId } : {}),
          ...(samePayloadOperation
            ? {
                preferredClientMessageId: samePayloadOperation.clientMessageId,
              }
            : {}),
        });
        operation = {
          // 編集内容のfingerprintは再アップロードを避けるためのcache key。
          // 送信IDは実送信payloadで判定する。空白の増減など実payloadが
          // 変わらない編集で新しいIDを振ると、応答消失後の再送で二重配信になる。
          clientMessageId: identity.clientMessageId,
          sourceKey: identity.sourceKey,
          sourceRevision: identity.sourceRevision,
          intentHash: identity.intentHash,
          requestedIntentHash: intentHash,
          sectionsFingerprint,
          optionsFingerprint,
          formattedSections,
          currentDraftId: identity.currentDraftId,
        };
        pendingBulkSendRef.current = operation;
      }

      const recoveryDecision = await recoverLineDispatch({
        sourceType: 'BULK',
        sourceKey: operation.sourceKey,
        sourceRevision: operation.sourceRevision,
        intentHash: operation.intentHash,
      });
      if (recoveryDecision === 'TERMINAL_FAILED') {
        clearSendOperationIdentity(
          'bulk',
          sendOperationOwnerKey,
          operation.clientMessageId,
        );
        pendingBulkSendRef.current = null;
        throw new Error(sendAllMessageStrings.sendFailed);
      }
      const recovered = recoveryDecision === 'RECOVERED';
      const result = recovered
        ? true
        : await sendMessageForAll(
            {
              sections: operation.formattedSections,
              sendOption: apiSendOption,
              currentDraftId: operation.currentDraftId,
              clientMessageId: operation.clientMessageId,
            },
            organizationId,
          );

      if (result === false) {
        toastError(new Error(sendAllMessageStrings.sendFailed));
        return;
      }
      if (!recovered) {
        await acknowledgeProducerResult({
          sourceType: 'BULK',
          sourceKey: operation.sourceKey,
          sourceRevision: operation.sourceRevision,
        });
      }

      if (currentDraftId) {
        setCurrentDraftId(undefined);
      }
      clearSendOperationIdentityByIntent(
        'bulk',
        sendOperationOwnerKey,
        operation.intentHash,
      );
      clearSendOperationIdentity(
        'bulk',
        sendOperationOwnerKey,
        operation.clientMessageId,
      );
      pendingBulkSendRef.current = null;
      toastSuccess(commonStrings.success);
      if (!isMountedRef.current) return;
      if (
        !recovered ||
        operation.requestedIntentHash === operation.intentHash
      ) {
        setSections([createSection(EContentSectionType.TEXT)]);
      }
      setOpenConfirm(false);
    } catch (error) {
      // 同じ送信IDへ別内容を送った拒否はproviderへ何も送っていない。
      // identityを残すと、応答を失った将来予約が実行されるまで
      // 同じブラウザから一斉送信できなくなる。
      // sendSourceAlreadyUsedは「そのsource keyを別intentまたは別人のdispatchが
      // 使い切っている」と確定した経路でだけ投げられる。keyを解放して新keyで
      // 送り直しても既存dispatchには触れず、別本文が旧dispatchへ吸収される経路は
      // backendのintent照合(409)が塞ぐため二重送信にならない。
      // 解放しないと次の送信も同じkeyで照合され、恒久的に送信不能になる。
      if (
        isSendOperationPayloadMismatch(error) ||
        (error instanceof Error &&
          error.message === sendAllMessageStrings.sendSourceAlreadyUsed)
      ) {
        const staleOperation = pendingBulkSendRef.current;
        if (staleOperation) {
          clearSendOperationIdentity(
            'bulk',
            sendOperationOwnerKey,
            staleOperation.clientMessageId,
          );
          pendingBulkSendRef.current = null;
        }
      }
      toastError(error);
    } finally {
      setIsLoading(false);
      endBusy();
    }
  }, [
    isMountedRef,
    beginBusy,
    endBusy,
    organizationId,
    sections,
    sendOption,
    currentDraftId,
    sendOperationOwnerKey,
    formatSections,
    toastError,
    toastSuccess,
    recoverLineDispatch,
    acknowledgeProducerResult,
    validateBulkMessageForm,
    validateDeliveryTarget,
  ]);

  const handleTestSendClick = useCallback(async () => {
    if (!hasAnyContent(sections)) {
      toastError(new Error(sendErrorMessages.noMediaInput));
      return;
    }
    if (!areMessageAssetSectionsValid(sections, assets, assetsReady)) {
      toastError(new Error(sendErrorMessages.invalidAssetSelection));
      return;
    }

    // 対象数を最新化してから確認モーダルを出す（設定直後の変更を反映する）
    // refetch はAPI失敗時も reject せず結果を返す。失敗時は過去の成功データが
    // data に残り得るため、isError も見て stale な人数でモーダルを開かない
    const result = await refetchTestDeliveryCount();
    if (result.isError || !result.data) {
      toastError(new Error(sendAllMessageStrings.testSend.countLoadFailed));
      return;
    }
    const { data } = result;
    if (data.count === 0) {
      toastError(new Error(sendAllMessageStrings.testSend.noTarget));
      return;
    }

    setConfirmTestCount(data.count);
    setOpenTestConfirm(true);
  }, [assets, assetsReady, sections, refetchTestDeliveryCount, toastError]);

  const handleTestSend = useCallback(async () => {
    beginBusy();
    try {
      setIsTestSending(true);

      if (!hasAnyContent(sections)) {
        toastError(new Error(sendErrorMessages.noMediaInput));
        return;
      }
      if (!areMessageAssetSectionsValid(sections, assets, assetsReady)) {
        toastError(new Error(sendErrorMessages.invalidAssetSelection));
        return;
      }

      const optionsFingerprint = 'test-send';
      const sectionsFingerprint = createSectionsFingerprint(sections);
      let operation = pendingTestSendRef.current;
      if (
        !operation ||
        operation.sectionsFingerprint !== sectionsFingerprint ||
        operation.optionsFingerprint !== optionsFingerprint
      ) {
        const formattedSections = await formatSections(sections);
        if (formattedSections.length === 0) {
          toastError(new Error(sendErrorMessages.noContent));
          return;
        }
        const hasSamePayload = isSameSendPayload(operation, {
          optionsFingerprint,
          formattedSections,
        });
        const samePayloadOperation = hasSamePayload ? operation : null;
        const intentHash = await createLineDispatchIntentHash({
          sections: formattedSections,
        });
        const identity = await claimSendOperationIdentity({
          kind: 'test',
          intentHash,
          sourceRevision: 1,
          ownerKey: sendOperationOwnerKey,
          ...(samePayloadOperation
            ? {
                preferredClientMessageId: samePayloadOperation.clientMessageId,
              }
            : {}),
        });
        operation = {
          // 一斉送信と同じく、実送信payloadが同一なら送信IDを維持する
          clientMessageId: identity.clientMessageId,
          sourceKey: identity.sourceKey,
          sourceRevision: identity.sourceRevision,
          intentHash: identity.intentHash,
          requestedIntentHash: intentHash,
          sectionsFingerprint,
          optionsFingerprint,
          formattedSections,
        };
        pendingTestSendRef.current = operation;
      }

      const recoveryDecision = await recoverLineDispatch({
        sourceType: 'TEST',
        sourceKey: operation.sourceKey,
        sourceRevision: operation.sourceRevision,
        intentHash: operation.intentHash,
      });
      if (recoveryDecision === 'TERMINAL_FAILED') {
        clearSendOperationIdentity(
          'test',
          sendOperationOwnerKey,
          operation.clientMessageId,
        );
        pendingTestSendRef.current = null;
        throw new Error(sendAllMessageStrings.sendFailed);
      }
      const recovered = recoveryDecision === 'RECOVERED';
      const result = recovered
        ? true
        : await sendTestMessageForAll(
            {
              sections: operation.formattedSections,
              clientMessageId: operation.clientMessageId,
            },
            organizationId,
          );

      if (result === false) {
        toastError(new Error(sendAllMessageStrings.sendFailed));
        return;
      }
      if (!recovered) {
        await acknowledgeProducerResult({
          sourceType: 'TEST',
          sourceKey: operation.sourceKey,
          sourceRevision: operation.sourceRevision,
        });
      }

      // 本配信につなげるため sections / currentDraftId はクリアしない
      clearSendOperationIdentityByIntent(
        'test',
        sendOperationOwnerKey,
        operation.intentHash,
      );
      clearSendOperationIdentity(
        'test',
        sendOperationOwnerKey,
        operation.clientMessageId,
      );
      pendingTestSendRef.current = null;
      toastSuccess(sendAllMessageStrings.testSend.success);
      if (!isMountedRef.current) return;
      setOpenTestConfirm(false);
      setConfirmTestCount(null);
    } catch (error) {
      // 一斉送信と同じく、今回の内容をproviderへ送っていないと確定した拒否
      // (keyの使い切り・内容不一致)だけidentityを解放する。
      // 残すとこの端末からテスト配信が恒久的に失敗する。
      if (
        isSendOperationPayloadMismatch(error) ||
        (error instanceof Error &&
          error.message === sendAllMessageStrings.sendSourceAlreadyUsed)
      ) {
        const staleOperation = pendingTestSendRef.current;
        if (staleOperation) {
          clearSendOperationIdentity(
            'test',
            sendOperationOwnerKey,
            staleOperation.clientMessageId,
          );
          pendingTestSendRef.current = null;
        }
      }
      toastError(error);
    } finally {
      setIsTestSending(false);
      endBusy();
    }
  }, [
    isMountedRef,
    beginBusy,
    endBusy,
    organizationId,
    assets,
    assetsReady,
    sections,
    sendOperationOwnerKey,
    formatSections,
    toastError,
    toastSuccess,
    recoverLineDispatch,
    acknowledgeProducerResult,
  ]);

  useEffect(() => {
    // 予約の編集が優先。両方を同時に読むと、予約編集モードのまま本文だけ
    // 下書きで上書きされ、下書きの内容を予約として保存できてしまう
    if (!draftId || reservationId) return;

    let cancelled = false;
    setIsLoadingDraft(true);
    const loadDraft = async () => {
      try {
        const draft = await getDraftMessage(draftId);
        if (cancelled) return;
        if (draft) {
          const normalizedSendOption = normalizeDraftSendOption(
            draft.sendOption,
          );
          if (!normalizedSendOption) {
            toastError(
              new Error(
                sendAllMessageStrings.drafts.messages.invalidSendOption,
              ),
            );
            return;
          }

          const loadedSections = convertDraftSectionsToMessageSections(
            draft.sections,
          );

          setSections(loadedSections);
          let scheduledDateTime: Date | undefined =
            normalizedSendOption.scheduledDateTime
              ? new Date(normalizedSendOption.scheduledDateTime)
              : undefined;
          if (
            scheduledDateTime &&
            !moment(scheduledDateTime).isAfter(moment())
          ) {
            scheduledDateTime = undefined;
          }
          setSendOption({
            ...normalizedSendOption,
            sendTime: scheduledDateTime
              ? normalizedSendOption.sendTime
              : ESendTime.IMMEDIATE,
            scheduledDateTime,
          });

          setCurrentDraftId(draft.id);
          navigate(orgPathFor(routes.SEND_MESSAGE_FOR_ALL), { replace: true });
        } else {
          toastError(new Error(sendAllMessageStrings.drafts.messages.notFound));
        }
      } catch (error) {
        if (cancelled) return;
        toastError(error);
      } finally {
        if (!cancelled) setIsLoadingDraft(false);
      }
    };

    beginBusy();
    void loadDraft().finally(() => endBusy());
    return () => {
      cancelled = true;
    };
  }, [draftId, reservationId, navigate, toastError, toastSuccess, beginBusy, endBusy, orgPathFor]);

  useEffect(() => {
    if (!reservationId) return;

    let cancelled = false;
    setIsLoadingReservation(true);
    const loadReservation = async () => {
      try {
        const reservation = await getReservation(reservationId);
        if (cancelled) return;
        // 一度配信を試みた予約は内容が確定済みで、書き換えても次の実行に反映されない
        if (!reservation.editable) {
          toastError(
            new Error(sendAllMessageStrings.reservations.notEditableHint),
          );
          navigate(orgPathFor(routes.RESERVATIONS), { replace: true });
          return;
        }

        const normalizedSendOption = normalizeDraftSendOption(
          reservation.sendOption,
        );
        if (!normalizedSendOption) {
          toastError(
            new Error(
              sendAllMessageStrings.reservations.messages.invalidSendOption,
            ),
          );
          // 編集モードに入れないまま送信画面に残すと、予約とは別の新規配信を
          // 始められてしまう。読み込めなかった場合は必ず一覧へ戻す
          navigate(orgPathFor(routes.RESERVATIONS), { replace: true });
          return;
        }

        setSections(
          convertDraftSectionsToMessageSections(reservation.sections),
        );
        // 予約の編集なので即時配信へは落とさない。日時が過去に流れていた場合は
        // 更新時にサーバが弾くため、利用者は日時を選び直すことになる
        setSendOption({
          ...normalizedSendOption,
          sendTime: ESendTime.SCHEDULED,
          scheduledDateTime: reservation.scheduledDateTime
            ? new Date(reservation.scheduledDateTime)
            : undefined,
        });
        setEditingReservationId(reservation.id);
        // 予約は下書きとは別の実体。読み込み前の下書きIDを引き継がない
        setCurrentDraftId(undefined);
        navigate(orgPathFor(routes.SEND_MESSAGE_FOR_ALL), { replace: true });
        toastSuccess(sendAllMessageStrings.reservations.messages.loaded);
      } catch (error) {
        if (cancelled) return;
        toastError(error);
        // 読み込めなかった場合も送信画面に残さない。cronが先に配信した予約や
        // 別タブで取り消された予約の404がここへ来る
        navigate(orgPathFor(routes.RESERVATIONS), { replace: true });
      } finally {
        if (!cancelled) setIsLoadingReservation(false);
      }
    };

    beginBusy();
    void loadReservation().finally(() => endBusy());
    return () => {
      cancelled = true;
    };
  }, [reservationId, navigate, toastError, toastSuccess, beginBusy, endBusy, orgPathFor]);

  const handleSaveDraft = useCallback(async () => {
    beginBusy();
    setIsSavingDraft(true);
    try {
      if (!validateBulkMessageForm(sections)) {
        return;
      }

      const formattedSections = await formatSections(sections);
      const scheduledDateTime =
        sendOption.scheduledDateTime == null
          ? undefined
          : typeof sendOption.scheduledDateTime === 'string'
            ? sendOption.scheduledDateTime
            : sendOption.scheduledDateTime.toISOString();

      const draftSendOption = {
        recipient: sendOption.recipient,
        tagIds:
          sendOption.recipient === ESendRecipient.FILTER
            ? sendOption.tagIds
            : undefined,
        sendTime: sendOption.sendTime,
        scheduledDateTime,
      };
      if (currentDraftId) {
        await updateDraftMessage(
          currentDraftId,
          { sections: formattedSections, sendOption: draftSendOption },
          organizationId,
        );
        toastSuccess(sendAllMessageStrings.drafts.messages.updated);
        if (!isMountedRef.current) return;
      } else {
        const newDraft = await saveDraftMessage(
          { sections: formattedSections, sendOption: draftSendOption },
          organizationId,
        );
        toastSuccess(sendAllMessageStrings.drafts.messages.saved);
        if (!isMountedRef.current) return;
        setCurrentDraftId(newDraft.id);
      }
      setOpenSaveModal(false);
      navigate(orgPathFor(routes.DRAFTS));
    } catch (error) {
      toastError(error);
    } finally {
      setIsSavingDraft(false);
      endBusy();
    }
  }, [
    isMountedRef,
    beginBusy,
    endBusy,
    organizationId,
    orgPathFor,
    sections,
    formatSections,
    toastError,
    toastSuccess,
    currentDraftId,
    sendOption,
    navigate,
    validateBulkMessageForm,
  ]);

  const handleUpdateReservationClick = useCallback(() => {
    // 予約編集では sendTime が常に SCHEDULED のため、配信予定日時の必須・
    // 未来日時の検証は validateBulkMessageForm 側で行われる
    if (!validateBulkMessageForm(sections)) return;
    if (!validateDeliveryTarget()) return;
    setOpenReservationConfirm(true);
  }, [sections, validateBulkMessageForm, validateDeliveryTarget]);

  const handleUpdateReservation = useCallback(async () => {
    if (!editingReservationId) return;

    beginBusy();
    setIsUpdatingReservation(true);
    try {
      const formattedSections = await formatSections(sections);
      if (formattedSections.length === 0) {
        toastError(new Error(sendErrorMessages.noContent));
        return;
      }

      await updateReservation(
        editingReservationId,
        {
          sections: formattedSections,
          sendOption: {
            recipient: sendOption.recipient,
            tagIds:
              sendOption.recipient === ESendRecipient.FILTER
                ? normalizeTagIds(sendOption.tagIds)
                : undefined,
            // 予約の編集では即時配信へ切り替えさせない
            sendTime: ESendTime.SCHEDULED,
            scheduledDateTime:
              sendOption.scheduledDateTime == null
                ? undefined
                : typeof sendOption.scheduledDateTime === 'string'
                  ? sendOption.scheduledDateTime
                  : sendOption.scheduledDateTime.toISOString(),
          },
        },
        organizationId,
      );

      toastSuccess(sendAllMessageStrings.reservations.messages.updated);
      if (!isMountedRef.current) return;
      setOpenReservationConfirm(false);
      navigate(orgPathFor(routes.RESERVATIONS));
    } catch (error) {
      toastError(error);
      if (!isMountedRef.current) return;
      // 別タブで取り消された(404)・cronが配信を始めた(409)予約は編集画面に
      // 残しても操作できないため一覧へ戻す。ネットワーク断や5xxは再試行できる
      // よう編集画面と確認モーダルに残す
      const status = getHttpStatus(error);
      if (status === 404 || status === 409) {
        setOpenReservationConfirm(false);
        navigate(orgPathFor(routes.RESERVATIONS), { replace: true });
      }
    } finally {
      setIsUpdatingReservation(false);
      endBusy();
    }
  }, [
    isMountedRef,
    beginBusy,
    endBusy,
    editingReservationId,
    formatSections,
    navigate,
    organizationId,
    orgPathFor,
    sections,
    sendOption,
    toastError,
    toastSuccess,
  ]);

  return (
    <>
      <section
        className={`w-full lg:h-[calc(100vh-12rem)] flex flex-col lg:overflow-hidden ${pageCardClasses.regular}`}
      >
        <header className="flex-shrink-0 flex flex-wrap items-center gap-4 mb-6">
          <Text bold className="text-2xl whitespace-nowrap">
            {sendAllMessageStrings.sendAll}
          </Text>
          <div data-testid="send-all-header-actions" className="flex flex-wrap gap-2">
            {isEditingReservation ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(orgPathFor(routes.RESERVATIONS))}
                disabled={isUpdatingReservation}
              >
                {sendAllMessageStrings.reservations.editing.cancelEdit}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(orgPathFor(routes.DRAFTS))}
                >
                  {sendAllMessageStrings.drafts.listTitle}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(orgPathFor(routes.RESERVATIONS))}
                >
                  {sendAllMessageStrings.reservations.listTitle}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenSaveModal(true)}
                  disabled={
                    isSavingDraft ||
                    isLoadingDraft ||
                    isLoadingReservation ||
                    isLoading ||
                    !canSaveDraft
                  }
                  loading={isSavingDraft}
                >
                  {sendAllMessageStrings.saveDraft}
                </Button>
              </>
            )}
          </div>
        </header>

        {isEditingReservation && (
          <div className="flex-shrink-0 mb-4 rounded-2xl border border-primary-200 bg-primary-50 px-4 py-3">
            <Text className="text-xs text-ink sm:text-sm">
              {sendAllMessageStrings.reservations.editing.banner}
            </Text>
          </div>
        )}

        <div className="flex flex-col gap-6 lg:flex-row lg:gap-10 flex-1 min-h-0">
          <div className="flex-1 lg:overflow-y-auto overflow-x-hidden lg:pr-2">
            {assetsLoadError ? (
              <div className="mb-4">
                <MessageAssetQueryError
                  isRetrying={isFetchingAssets}
                  onRetry={() => {
                    void refetchAssets();
                  }}
                />
              </div>
            ) : null}
            <BulkMessageEditor
              onCanSaveDraftChange={setCanSaveDraft}
              sections={sections}
              assets={assets}
              assetsReady={assetsReady}
              sendOption={sendOption}
              onSectionsChange={setSections}
              onSendOptionChange={(option) =>
                setSendOption({
                  ...sendOption,
                  ...option,
                  // 予約の編集は予約のまま更新する。UIの無効化だけに頼らない
                  ...(isEditingReservation
                    ? { sendTime: ESendTime.SCHEDULED }
                    : {}),
                })
              }
              onSend={
                isEditingReservation
                  ? handleUpdateReservationClick
                  : handleSendClick
              }
              onTestSend={handleTestSendClick}
              isLoading={
                isLoading || isUpdatingReservation || isReservationLoadPending
              }
              sendLabel={
                isEditingReservation
                  ? sendAllMessageStrings.reservations.editing.submit
                  : isScheduledSend
                    ? sendAllMessageStrings.schedule.submit
                    : undefined
              }
              disableImmediateSend={isEditingReservation}
              isTestLoading={isTestSending}
              deliveryCount={deliveryCount}
              isDeliveryCountLoading={isDeliveryCountLoading}
              isDeliveryCountError={isDeliveryCountError}
              isDeliveryCountReady={isDeliveryCountReady}
              onRetryDeliveryCount={() => {
                void refetchDeliveryCount();
              }}
              testDeliveryCount={testDeliveryCount}
            />
          </div>
          <div className="flex items-center justify-center w-full h-[460px] sm:h-[540px] lg:h-full lg:w-[400px] lg:min-h-[700px] xl:-mt-[70px]">
            <div
              className="relative w-full max-w-[260px] sm:max-w-[300px] lg:max-w-[375px] mx-auto h-full lg:mx-0 lg:mr-2 mb-2 rounded-[3rem] border-8 border-white
             shadow-2xl flex flex-col overflow-hidden preview-phone-shadow z-10"
            >
              <MessagePreview sections={sections} assets={assets} enableNameInsertionPreview />
            </div>
          </div>
        </div>

        <ConfirmModal
          textTitle={confirmStrings.title}
          textMessage={confirmStrings.message}
          textCancel={commonStrings.cancel}
          textConfirm={
            isScheduledSend
              ? sendAllMessageStrings.schedule.confirm
              : commonStrings.send
          }
          onClose={() => {
            if (pendingBulkSendRef.current?.currentDraftId) {
              setCurrentDraftId(undefined);
            }
            // 閉じる操作だけではserver未受付と断定できないため、曖昧失敗後の
            // operation identityは保持する。本文・送信条件の変更時にだけ置き換える。
            setOpenConfirm(false);
          }}
          onConfirm={handleSend}
          open={openConfirm}
          isLoading={isLoading}
        />

        <ConfirmModal
          textTitle={sendAllMessageStrings.testSend.title}
          textMessage={sendAllMessageStrings.testSend.content.replace(
            '${count}',
            (confirmTestCount ?? 0).toLocaleString('ja-JP'),
          )}
          textCancel={commonStrings.cancel}
          textConfirm={commonStrings.send}
          onClose={() => {
            // 本配信と同様、response喪失後に開き直しても同じoperationを再試行する。
            setOpenTestConfirm(false);
            setConfirmTestCount(null);
          }}
          onConfirm={handleTestSend}
          open={openTestConfirm}
          isLoading={isTestSending}
        />

        <ConfirmModal
          textTitle={sendAllMessageStrings.reservations.updateConfirm.title}
          textMessage={sendAllMessageStrings.reservations.updateConfirm.message}
          textCancel={commonStrings.cancel}
          textConfirm={sendAllMessageStrings.reservations.editing.submit}
          onClose={() => setOpenReservationConfirm(false)}
          onConfirm={handleUpdateReservation}
          open={openReservationConfirm}
          isLoading={isUpdatingReservation}
        />

        <SaveDraftModal
          open={openSaveModal}
          onClose={() => setOpenSaveModal(false)}
          onConfirm={handleSaveDraft}
          isLoading={isSavingDraft}
        />
      </section>
    </>
  );
}

export default SendMessageForAll;
